// src/pages/trading-portal/TradeExecution/AIAgentPnLReportPage.js
// Standalone P&L Report page for all AI Trading Agents

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Skeleton, Alert, Grid, Card,
    CardContent, Chip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const headCellSx = {
    fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem',
    textTransform: 'uppercase', letterSpacing: '0.05em',
};
const bodyCellSx = { fontFamily, fontSize: '0.85rem', color: '#334155' };

const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color, fontSize: 24 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>{title}</Typography>
                    <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>{value}</Typography>
                    {subtitle && <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{subtitle}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const AIAgentPnLReportPage = () => {
    const [canView, setCanView] = useState(false);

    const [pnlData, setPnlData] = useState(null);
    const [pnlLoading, setPnlLoading] = useState(false);
    const [pnlPage, setPnlPage] = useState(0);
    const [pnlPerPage, setPnlPerPage] = useState(20);
    const [pnlTotal, setPnlTotal] = useState(0);

    const [agents, setAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState('');

    useEffect(() => {
        const checkPerms = async () => {
            setCanView(await hasPermission('Trade Execution Engine', 'can_view'));
        };
        checkPerms();
    }, []);

    const loadPnlReport = useCallback(async (agentId, page = 1, perPage = 20) => {
        setPnlLoading(true);
        try {
            let url = `/api/trade-execution/ai-agents/pnl-report?page=${page}&limit=${perPage}`;
            if (agentId) url += `&agent_id=${agentId}`;
            const res = await axiosInstance.get(url);
            setPnlData(res.data.data || null);
            setPnlTotal(res.data.data?.pagination?.total || 0);
            if (res.data.data?.agents) setAgents(res.data.data.agents);
        } catch (err) {
            console.error('Failed to load P&L report:', err.response?.data?.error || err.response?.data?.message || err.message);
        } finally {
            setPnlLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) loadPnlReport(selectedAgentId, pnlPage + 1, pnlPerPage);
    }, [canView, selectedAgentId, pnlPage, pnlPerPage, loadPnlReport]);

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>You do not have permission to view AI Agent P&L Reports.</Alert>
            </Box>
        );
    }

    const summary = pnlData?.summary || {};
    const trades = pnlData?.trades || [];
    const pnlColor = (val) => val > 0 ? '#16a34a' : val < 0 ? '#dc2626' : '#64748b';

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.5rem', color: '#1e293b' }}>
                        AI Agent P&L Reports
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                        Trade-level profit & loss across all AI trading agents
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel sx={{ fontFamily }}>Filter by Agent</InputLabel>
                    <Select
                        value={selectedAgentId}
                        label="Filter by Agent"
                        onChange={(e) => { setSelectedAgentId(e.target.value); setPnlPage(0); }}
                        sx={{ fontFamily, borderRadius: '12px' }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All Agents</MenuItem>
                        {agents.map((a) => (
                            <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>
                                {a.name} ({a.symbol || '—'})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2}>
                    <StatCard icon={MuiIcons.AccountBalance} title="Total P&L" value={`$${(summary.total_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color={pnlColor(summary.total_pnl)} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <StatCard icon={MuiIcons.TrendingUp} title="Win Rate" value={`${summary.win_rate || 0}%`} color={(summary.win_rate || 0) >= 50 ? '#16a34a' : '#dc2626'} subtitle={`${summary.winning_trades || 0}W / ${summary.losing_trades || 0}L`} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <StatCard icon={MuiIcons.SwapVert} title="Total Trades" value={summary.total_trades || 0} color="#6366f1" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <StatCard icon={MuiIcons.ArrowUpward} title="Best Trade" value={`$${(summary.best_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#16a34a" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <StatCard icon={MuiIcons.ArrowDownward} title="Worst Trade" value={`$${(summary.worst_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#dc2626" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <StatCard icon={MuiIcons.Functions} title="Avg P&L / Trade" value={`$${(summary.avg_pnl_per_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color={pnlColor(summary.avg_pnl_per_trade)} subtitle={`PF: ${summary.profit_factor || 0}`} />
                </Grid>
            </Grid>

            {/* Trades Table */}
            <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {pnlLoading ? (
                    <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: '12px' }} /></Box>
                ) : trades.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <MuiIcons.ReceiptLong sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>
                            {selectedAgentId ? 'No closed trades for this agent' : 'No closed trades across any agents yet'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Exit Date</TableCell>
                                        <TableCell sx={headCellSx}>Agent</TableCell>
                                        <TableCell sx={headCellSx}>Symbol</TableCell>
                                        <TableCell sx={headCellSx}>Side</TableCell>
                                        <TableCell sx={headCellSx} align="right">Entry</TableCell>
                                        <TableCell sx={headCellSx} align="right">Exit</TableCell>
                                        <TableCell sx={headCellSx} align="right">Shares</TableCell>
                                        <TableCell sx={headCellSx} align="right">P&L ($)</TableCell>
                                        <TableCell sx={headCellSx} align="right">P&L (%)</TableCell>
                                        <TableCell sx={headCellSx} align="right">Duration</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {trades.map((t) => (
                                        <TableRow key={`${t.agent_id}-${t.id}`} hover>
                                            <TableCell sx={bodyCellSx}>{t.exit_date ? new Date(t.exit_date).toLocaleString() : '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={t.agent_name} size="small" variant="outlined" sx={{ fontFamily, fontWeight: 500 }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}><strong>{t.symbol}</strong></TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={t.side} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: t.side === 'LONG' ? '#dcfce7' : '#fee2e2', color: t.side === 'LONG' ? '#16a34a' : '#dc2626' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx} align="right">${t.entry_price?.toFixed(2)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">${t.exit_price?.toFixed(2)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{t.quantity}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: pnlColor(t.pnl) }} align="right">
                                                {t.pnl > 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, color: pnlColor(t.pnl_pct) }} align="right">
                                                {t.pnl_pct > 0 ? '+' : ''}{t.pnl_pct?.toFixed(2)}%
                                            </TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {t.duration_minutes ? `${Math.floor(t.duration_minutes / 60)}h ${t.duration_minutes % 60}m` : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={pnlTotal} page={pnlPage} rowsPerPage={pnlPerPage}
                            onPageChange={(_, p) => setPnlPage(p)}
                            onRowsPerPageChange={(e) => { setPnlPerPage(parseInt(e.target.value, 10)); setPnlPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default AIAgentPnLReportPage;
